import React, { useEffect, useMemo, useState } from 'react';
import { typography } from '../../styles/typography';
import { Card, IconButton, PrimaryButton, ProfileImage, SearchBar } from '../ui';

// Memoized ProfileImage wrapper to prevent re-renders
const MemoizedProfileImage = React.memo(ProfileImage, (prevProps, nextProps) => {
  return (
    prevProps.userId === nextProps.userId &&
    prevProps.imagePath === nextProps.imagePath &&
    prevProps.userName === nextProps.userName
  );
});
MemoizedProfileImage.displayName = 'MemoizedProfileImage';

// Memoized EntityCard component
const EntityCard = React.memo(({ entity, isSelected, onToggle, entityType }) => {
  const getEntityId = () => {
    switch (entityType.toLowerCase()) {
      case 'employee':
        return entity.user_id || entity.employee_id || '';
      case 'vehicle':
        return entity.vehicle_id || entity.id || '';
      case 'site':
        return entity.site_id || entity.id || '';
      default:
        return '';
    }
  };

  const getEntityTitle = () => {
    switch (entityType.toLowerCase()) {
      case 'employee':
        return entity.user_name || entity.name || entity.display_name || 'Unknown';
      case 'vehicle':
        return entity.registration_no || entity.registration_number || 'Unknown';
      case 'site':
        return entity.site_name || 'Unknown';
      default:
        return 'Unknown';
    }
  };

  const getEntitySubtitle = () => {
    switch (entityType.toLowerCase()) {
      case 'employee':
        return `${entity.spec_role || entity.role || 'Employee'} • ID: ${entity.employee_id || entity.user_id || 'N/A'}`;
      case 'vehicle':
        return `${entity.vehicle_type || 'Vehicle'} • ID: ${entity.vehicle_id || entity.id || 'N/A'}`;
      case 'site':
        return `Location: ${entity.address || 'N/A'}`;
      default:
        return '';
    }
  };

  const title = getEntityTitle();
  const subtitle = getEntitySubtitle();

  // Memoize ProfileImage for employees
  const profileImage = useMemo(() => {
    if (entityType.toLowerCase() === 'employee') {
      return (
        <MemoizedProfileImage
          key={`profile-${entity.user_id || entity.employee_id}`}
          userId={entity.user_id || entity.employee_id}
          userName={title}
          imagePath={entity.image_path}
          size="md"
          showLoading={false}
        />
      );
    }
    return null;
  }, [entity.user_id, entity.employee_id, title, entity.image_path, entityType]);

  return (
    <div>
      {isSelected ? (
        <Card.Gradient 
          className="p-3 cursor-pointer transition-all duration-200 hover:shadow-lg"
          onClick={() => onToggle(entity)}
        >
          <div className="flex justify-between items-center">
            <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
              {entityType.toLowerCase() === 'employee' ? profileImage : (
                entityType.toLowerCase() === 'vehicle' ? (
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 text-xl">🚗</span>
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 text-xl">📍</span>
                  </div>
                )
              )}
              <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                <div className="w-full text-black text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {title}
                </div>
                <div className="w-full text-black text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {subtitle}
                </div>
              </div>
            </div>
            <div 
              data-property-1="check" 
              className="w-6 h-6 relative flex-shrink-0 ml-3"
            >
              <div className="w-6 h-6 left-0 top-0 absolute bg-green-700 rounded-sm flex items-center justify-center">
                <svg 
                  className="w-4 h-4 text-white"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </Card.Gradient>
      ) : (
        <Card.Dark 
          className="p-3 cursor-pointer transition-all duration-200 hover:shadow-lg"
          onClick={() => onToggle(entity)}
        >
          <div className="flex justify-between items-center">
            <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
              {entityType.toLowerCase() === 'employee' ? profileImage : (
                entityType.toLowerCase() === 'vehicle' ? (
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 text-xl">🚗</span>
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 text-xl">📍</span>
                  </div>
                )
              )}
              <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                <div className="w-full text-white text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {title}
                </div>
                <div className="w-full text-gray-300 text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {subtitle}
                </div>
              </div>
            </div>
            <div 
              data-property-1="Default" 
              className="w-6 h-6 relative flex-shrink-0 ml-3"
            >
              <div className="w-6 h-6 left-0 top-0 absolute rounded-sm bg-zinc-100 border-[1.60px] border-black/30" />
            </div>
          </div>
        </Card.Dark>
      )}
    </div>
  );
});

const MultiSelectDialog = ({
  isOpen,
  onClose,
  onConfirm,
  entityType,
  dialogTitle,
  availableEntities,
  selectedEntityIds = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set(selectedEntityIds));
  const [isMobileView, setIsMobileView] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setFilteredEntities(availableEntities);
      setSearchTerm('');
      setSearchSuggestions([]);
      setSelectedIds(new Set(selectedEntityIds));
    }
  }, [isOpen, availableEntities, selectedEntityIds]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredEntities(availableEntities);
      setSearchSuggestions([]);
      return;
    }

    const query = searchTerm.toLowerCase();
    const filtered = availableEntities.filter(entity => {
      switch (entityType.toLowerCase()) {
        case 'employee':
          const id = (entity.user_id || entity.employee_id || '').toLowerCase();
          const name = (entity.user_name || entity.name || entity.display_name || '').toLowerCase();
          const role = (entity.spec_role || entity.role || '').toLowerCase();
          return id.includes(query) || name.includes(query) || role.includes(query);
        case 'vehicle':
          const vehicleId = (entity.vehicle_id || entity.id || '').toLowerCase();
          const registrationNo = (entity.registration_no || entity.registration_number || '').toLowerCase();
          const model = (entity.model_name || '').toLowerCase();
          return vehicleId.includes(query) || registrationNo.includes(query) || model.includes(query);
        case 'site':
          const siteId = (entity.site_id || entity.id || '').toLowerCase();
          const siteName = (entity.site_name || '').toLowerCase();
          const siteType = (entity.site_type || '').toLowerCase();
          return siteId.includes(query) || siteName.includes(query) || siteType.includes(query);
        default:
          return true;
      }
    });
    setFilteredEntities(filtered);
    setSearchSuggestions(filtered.slice(0, 5).map(entity => {
      switch (entityType.toLowerCase()) {
        case 'employee':
          return entity.user_name || entity.name || entity.display_name || '';
        case 'vehicle':
          return entity.registration_no || entity.registration_number || '';
        case 'site':
          return entity.site_name || '';
        default:
          return '';
      }
    }));
  }, [searchTerm, availableEntities, entityType]);

  const getEntityId = (entity) => {
    switch (entityType.toLowerCase()) {
      case 'employee':
        return entity.user_id || entity.employee_id || '';
      case 'vehicle':
        return entity.vehicle_id || entity.id || '';
      case 'site':
        return entity.site_id || entity.id || '';
      default:
        return '';
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleSearchSubmit = (value) => {
    console.log('Search submitted:', value);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
    // Find the entity by name and filter to show only that entity
    const selectedEntity = availableEntities.find(entity => {
      switch (entityType.toLowerCase()) {
        case 'employee':
          return (entity.user_name || entity.name || entity.display_name || '') === suggestion;
        case 'vehicle':
          return (entity.registration_no || entity.registration_number || '') === suggestion;
        case 'site':
          return (entity.site_name || '') === suggestion;
        default:
          return false;
      }
    });
    if (selectedEntity) {
      setFilteredEntities([selectedEntity]);
    }
  };

  const handleToggleEntity = (entity) => {
    const entityId = getEntityId(entity);
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entityId)) {
        newSet.delete(entityId);
      } else {
        newSet.add(entityId);
      }
      return newSet;
    });
  };

  const handleCloseDialog = () => {
    onClose();
    setSearchTerm('');
    setSearchSuggestions([]);
    setFilteredEntities(availableEntities);
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds));
    handleCloseDialog();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Dialog Box */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`${isMobileView ? 'w-full max-w-full h-[90vh]' : 'w-[464px] h-[682px]'} relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden flex flex-col`}>
          {/* Header - Changed from absolute to flex layout for better mobile support */}
          <div className={`${isMobileView ? 'relative px-4 pt-4 pb-2' : 'absolute left-[32px] top-[24px] w-96'} flex justify-between items-start flex-shrink-0`}>
            <div className="flex flex-col flex-1 min-w-0 pr-2">
              <h3 className={typography.h3} style={{color: '#000000'}}>
                {dialogTitle}
              </h3>
              {selectedIds.size > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedIds.size} {entityType.toLowerCase()}{selectedIds.size !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            {/* Close Button */}
            <IconButton 
              onClick={handleCloseDialog}
              className="flex-shrink-0"
              icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              }
            />
          </div>

          {/* Dialog Content */}
          <div className={`${isMobileView ? 'px-4 mt-2' : 'mt-20 px-8'} flex-1 flex flex-col min-h-0`}>
            {/* Search Bar in Dialog */}
            <div className="mb-6 flex-shrink-0">
              <SearchBar
                placeholder={`Search ${entityType.toLowerCase()}s...`}
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={handleSearchSubmit}
                suggestions={searchSuggestions}
                onSuggestionClick={handleSuggestionClick}
                className="w-full" 
              />
            </div>

            {/* Entity List */}
            <div className={`space-y-3 ${isMobileView ? 'flex-1 overflow-y-auto' : 'max-h-[400px] overflow-y-auto'} pr-2`}>
              {filteredEntities.length > 0 ? (
                filteredEntities.map((entity, index) => {
                  const entityId = getEntityId(entity);
                  const isSelected = selectedIds.has(entityId);
                  
                  return (
                    <EntityCard
                      key={entityId || `entity-${index}`}
                      entity={entity}
                      isSelected={isSelected}
                      onToggle={handleToggleEntity}
                      entityType={entityType}
                    />
                  );
                })
              ) : searchTerm.length > 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No {entityType.toLowerCase()}s found matching "{searchTerm}"
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No {entityType.toLowerCase()}s available
                </div>
              )}
            </div>

            {/* Dialog Actions */}
            <div className={`${isMobileView ? 'relative mt-4 pb-4' : 'absolute bottom-6 left-8 right-8'} flex gap-3 justify-center flex-shrink-0`}>
              <PrimaryButton 
                onClick={handleConfirm}
                disabled={selectedIds.size === 0}
                className={isMobileView ? 'w-full' : ''}
              >
                Confirm {selectedIds.size > 0 && `(${selectedIds.size})`}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MultiSelectDialog;
